import { useMutation } from '@apollo/client';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { useCallback, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Trans, useTranslation } from 'react-i18next';
import styles from '../sass/components/LocationNotFoundModal.module.scss';
import { getFieldErrors } from '../util/getFieldErrors';
import { currentPresetBrand } from '../util/presetBrand';
import { graphqlErrorResponse } from '../util/validationErrorHelper';
import { localizeServerError } from '../i18n/errors';
import Alert from './Alert';
import { CREATE_BOOKING_ASSISTANCE_NOTIFICATION } from '../util/gql';
import Button from './Button';
import ModalCloseButton from './ModalCloseButton';
import TextArea from './TextArea';
import TextInput from './TextInput';

interface LocationNotFoundModalProps {
  postalCode: string;
  onTryAnotherPostalCode: () => void;
  onSubmit: () => void;
  /**
   * Where to send the visitor to join the waitlist. Given only for a tenant
   * whose marketing site has one (util/marketingSite); absent, the modal is
   * exactly what it always was.
   *
   * With it, the modal never says the address is not served: the market is
   * "at capacity" and the waitlist is the offer (the owner's rule for every
   * uncovered address on the Aperio preset). The copy below is fixed; it
   * does not name the city.
   */
  waitlistUrl?: string | null;
  /**
   * The address as one line, sent with a one-tap waitlist join (owner's
   * brief, 2026-11-01: "the at-capacity screen should offer a one-tap
   * waitlist join"; 220 checked, only 34 joined through the old link).
   */
  addressText?: string | null;
  /** The signed-in customer's email: then the join is one tap. */
  knownEmail?: string | null;
}

const EMAIL = /^[^\s@<>"(),;:]+@[^\s@<>"(),;:]+\.[a-z]{2,}$/i;

/**
 * The waitlist join right in the modal: the address is already known, so
 * a signed-in customer joins with one tap and anyone else types only an
 * email. Posts the site's own sign-up (POST /api/waitlist); if that fails,
 * the marketing site's waitlist page is still linked.
 */
function QuickWaitlist({
  postalCode,
  addressText,
  waitlistUrl,
  knownEmail,
}: {
  postalCode: string;
  addressText?: string | null;
  waitlistUrl: string;
  knownEmail?: string | null;
}) {
  const { t } = useTranslation();
  const known = EMAIL.test(String(knownEmail || '').trim())
    ? String(knownEmail).trim()
    : '';
  const [email, setEmail] = useState(known);
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>(
    'idle',
  );
  const save = async () => {
    const address = email.trim();
    if (!EMAIL.test(address)) {
      setState('error');
      return;
    }
    setState('busy');
    try {
      const base = process.env.REACT_APP_SERVER_URL || '';
      const res = await fetch(`${base}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: address,
          form: 'waitlist',
          src: 'portal-capacity',
          postal: postalCode,
          ...(addressText ? { address: addressText } : {}),
        }),
      });
      setState(res.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };
  if (state === 'done') {
    return (
      <p role="status" data-testid="waitlist-saved">
        <strong>{t('assistance.capacity.saved')}</strong>
      </p>
    );
  }
  return (
    <div>
      {!known && (
        <TextInput
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          title={t('assistance.capacity.emailLabel')}
          id="waitlist-email"
          type="email"
          autoComplete="email"
        />
      )}
      <Button
        variant="primary"
        onClick={save}
        loading={state === 'busy'}
        inactive={state === 'busy'}
      >
        {t('assistance.capacity.save')}
      </Button>
      {state === 'error' && (
        <p role="alert">
          <Trans
            t={t}
            i18nKey="assistance.capacity.saveFailed"
            components={{
              // eslint-disable-next-line jsx-a11y/anchor-has-content, max-len, jsx-a11y/control-has-associated-label
              link: <a href={waitlistUrl} />,
            }}
          />
        </p>
      )}
    </div>
  );
}

export default function LocationNotFoundModal({
  postalCode,
  onTryAnotherPostalCode,
  onSubmit,
  waitlistUrl,
  addressText,
  knownEmail,
}: LocationNotFoundModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [constraints, setConstraints] = useState([]);
  const [createBookingAssistanceNotification, {
    loading,
  }] = useMutation(CREATE_BOOKING_ASSISTANCE_NOTIFICATION);

  const { executeRecaptcha } = useGoogleReCaptcha();

  // Where to reach a person when the form cannot be sent. The preset's own
  // channels on Aperio; Go Oil's otherwise.
  const brand = currentPresetBrand();
  const contactPhone = brand?.phone ?? '1-833-466-4520';
  const contactPhoneHuman = brand?.phoneHuman ?? '1-833-466-4520';
  const contactEmail = brand?.email ?? 'support@gooil.ca';
  const [sendError, setSendError] = useState('');
  // One request at a time: a second click while one is out does nothing.
  const sending = useRef(false);

  const sendMessage = useCallback(async (token: string) => {
    try {
      await createBookingAssistanceNotification({
        variables: {
          name,
          phoneNumber: phone,
          email,
          body: messageBody,
          recaptchaToken: token,
          postalCode,
        },
      });
    } catch (ex) {
      // Not every error carries `extensions.response` (the reCAPTCHA
      // module's own exception does not): reading it blindly threw here and
      // the visitor saw nothing (QA 2026-09-25 #3). Say what happened, and
      // how else to reach us.
      const response = graphqlErrorResponse(ex);
      if (response?.statusCode === 422 && Array.isArray(response.message)) {
        setConstraints(response.message as never[]);
        return;
      }
      setSendError(typeof response?.message === 'string'
        && response?.error === 'RECAPTCHA_FAILED'
        ? localizeServerError(response.error, response.message)
        : t('assistance.sendFailed'));
      return;
    }
    onSubmit();
  },
  [createBookingAssistanceNotification,
    email,
    messageBody,
    name,
    onSubmit,
    phone,
    postalCode,
    t]);

  const handleReCaptchaVerify = useCallback(async () => {
    if (sending.current) return;
    sending.current = true;
    setSendError('');
    setConstraints([]);
    try {
      // No reCAPTCHA (blocked, or the key refuses this domain) no longer
      // drops the message on the floor: it goes without a token and the
      // server decides (it keeps it for review, or says to call us).
      let token = '';
      try {
        token = executeRecaptcha
          ? (await executeRecaptcha('BookingAssistance')) || ''
          : '';
      } catch (e) {
        token = '';
      }
      await sendMessage(token);
    } finally {
      sending.current = false;
    }
  }, [executeRecaptcha, sendMessage]);

  return (
    <div className={styles.modal}>
      <ModalCloseButton onClose={onTryAnotherPostalCode} />
      <div className={styles.header}>
        <Button
          variant="tertiary"
          onClick={onTryAnotherPostalCode}
        >
          <ChevronLeft />
          {t('assistance.backToBooking')}
        </Button>
      </div>
      <div className={styles.body}>

        {waitlistUrl && (
          <div className="apr-waitlist" data-testid="waitlist-offer">
            <h4>{t('assistance.capacity.title')}</h4>
            <p>{t('assistance.capacity.body')}</p>
            <QuickWaitlist
              postalCode={postalCode}
              addressText={addressText}
              waitlistUrl={waitlistUrl}
              knownEmail={knownEmail}
            />
          </div>
        )}

        <h4>
          {waitlistUrl
            ? t('assistance.oneOff.title')
            : "Let's try to book you an appointment"}
        </h4>
        {waitlistUrl ? (
          <p>{t('assistance.oneOff.body')}</p>
        ) : (
          <p>
            Sorry, the postal code you provided is outside of our default
            service area, but that doesn&apos;t mean we can&apos;t help!
            Contact our team to see if we can still make an
            appointment happen for you.
          </p>
        )}
        <div>
          <form>
            <div className={styles.upperForm}>
              <TextInput
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                title={t('assistance.form.name')}
                id="name"
                className={styles.formElement}
                errorMessage={getFieldErrors(constraints, ['message', 'name'])}
              />
              <TextInput
                value={postalCode}
                onChange={() => undefined}
                title={t('assistance.form.postalCode')}
                id="postal-code"
                disabled
                className={styles.formElement}
              />
              <TextInput
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                title={t('assistance.form.email')}
                id="email"
                className={styles.formElement}
                errorMessage={getFieldErrors(constraints, ['message', 'email'])}
              />
              <TextInput
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                title={t('assistance.form.phone')}
                id="phone"
                className={styles.formElement}
                errorMessage={
                  getFieldErrors(constraints, ['message', 'phoneNumber'])
                }
              />
            </div>
            <TextArea
              value={messageBody}
              onChange={(e) => setMessageBody(e.currentTarget.value)}
              title={t('assistance.form.message')}
              id="message"
              rows={4}
            />
          </form>
          {sendError && (
            <div role="alert" className="apr-form-error">
              <Alert message={sendError} type="error" />
              <p>
                <Trans
                  t={t}
                  i18nKey="assistance.callUs"
                  values={{ phone: contactPhoneHuman, email: contactEmail }}
                  components={{
                    // eslint-disable-next-line jsx-a11y/anchor-has-content, max-len, jsx-a11y/control-has-associated-label
                    tel: <a href={`tel:${contactPhone}`} />,
                    // eslint-disable-next-line jsx-a11y/anchor-has-content, max-len, jsx-a11y/control-has-associated-label
                    mail: <a href={`mailto:${contactEmail}`} />,
                  }}
                />
              </p>
            </div>
          )}
          <div className={styles.actionButtons}>
            <Button
              variant="tertiary"
              onClick={onTryAnotherPostalCode}
              className={styles.tryAnotherButton}
            >
              {t('assistance.tryAnother')}
            </Button>
            <Button
              variant="primary"
              onClick={handleReCaptchaVerify}
              className={styles.submitButton}
              loading={loading}
              inactive={loading}
            >
              {t('assistance.send')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
